import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { listInsights } from "../lib/api";
import { useT } from "../lib/i18n";

function formatDate(iso, lang) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(lang === "it" ? "it-CH" : "en-CH", { month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}

export default function Insights() {
    const { t, lang } = useT();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = lang === "it" ? "Approfondimenti, Casati Consulting" : "Insights, Casati Consulting";
        setLoading(true);
        listInsights(lang)
            .then((d) => setPosts(d))
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, [lang]);

    return (
        <div className="bg-[#F9F9F7] text-[#1C1C1C]">
            <section className="pt-20 md:pt-28 pb-12">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-8 md:gap-x-8">
                    <div className="col-span-12 md:col-span-8">
                        <p className="eyebrow">{t.insights.eyebrow}</p>
                        <h1
                            className="font-display mt-6 text-5xl sm:text-7xl lg:text-[100px] tracking-tight font-medium leading-[0.92]"
                            style={{ letterSpacing: "-0.035em" }}
                            data-testid="insights-title"
                        >
                            {t.insights.titleA}
                            <br />
                            <span className="italic font-light">{t.insights.titleB}</span>
                        </h1>
                    </div>
                    <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-[#1C1C1C]/70 leading-relaxed text-base md:text-lg">
                        {t.insights.intro}
                    </p>
                </div>
            </section>

            <section className="border-t hair">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    {renderInsightsList({ loading, posts, t, lang })}
                </div>
            </section>
        </div>
    );
}

function renderInsightsList({ loading, posts, t, lang }) {
    if (loading) {
        return (
            <p className="py-16 text-[#1C1C1C]/60" data-testid="insights-loading">
                {t.common.loading}
            </p>
        );
    }
    if (posts.length === 0) {
        return <p className="py-16 text-[#1C1C1C]/60">{t.common.noArticles}</p>;
    }
    return (
        <ul className="divide-y hair">
            {posts.map((p) => (
                <li key={p.slug}>
                    <Link
                        to={`/insights/${p.slug}`}
                        className="group grid grid-cols-12 gap-4 py-8 md:py-10 hover:bg-white -mx-2 px-2 transition-colors"
                        data-testid={`insights-row-${p.slug}`}
                    >
                        <div className="col-span-12 md:col-span-2 flex md:flex-col gap-3 md:gap-1">
                            <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#9B7A58" }}>
                                {p.category}
                            </span>
                            <span className="text-xs text-[#1C1C1C]/50">
                                {formatDate(p.published_at, lang)}
                            </span>
                        </div>
                        <div className="col-span-12 md:col-span-8">
                            <h2 className="font-display text-2xl md:text-3xl tracking-tight font-medium leading-tight" style={{ letterSpacing: "-0.02em" }}>
                                {p.title}
                            </h2>
                            <p className="mt-3 text-[#1C1C1C]/70 leading-relaxed text-[15px] max-w-2xl">
                                {p.excerpt}
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-2 flex md:justify-end items-end">
                            <span className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] font-bold text-[#1C1C1C]/60 group-hover:text-[#1C1C1C] transition-colors">
                                {p.read_minutes} {t.common.minRead}
                                <ArrowUpRight size={14} strokeWidth={1.5} />
                            </span>
                        </div>
                    </Link>
                </li>
            ))}
        </ul>
    );
}
